import { useCallback, useMemo, useState, type FormEvent } from 'react';
import type { AlertRule } from '@detect/shared';
import { ruleService, chainService } from '../../services/alerting';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { resourceService } from '../../services/resources';
import {
  CHANGE_NOT_RECOVERED_DEFAULT,
  CHANGE_NOT_RECOVERED_OVERRIDE,
  DETECTION_MODE_OPTIONS,
  describeRuleKinds,
  describeRuleResources,
  type DetectionMode,
} from './ruleKinds';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';

export function RulesTab() {
  const { tenantId, hasRole } = useTenant();
  const [editing, setEditing] = useState<WithId<AlertRule> | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      ruleService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<AlertRule>>(fetchPage, 'all');
  const resourcesQuery = usePickerOptions(['resources'], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter })
  );
  const resourceNames = useMemo(
    () => new Map((resourcesQuery.data?.items ?? []).map((r) => [r.id, r.displayName ?? r.name] as const)),
    [resourcesQuery.data]
  );

  const remove = async (rule: WithId<AlertRule>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete alert rule ${rule.name}?`)) return;
    setActionError(null);
    try {
      await ruleService.remove(tenantId, rule.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {hasRole('admin') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing('new')}
          >
            New rule
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No alert rules"
          description="Rules route alerts to escalation chains — without one, alerts stay unrouted."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Name</th>
                <th>Min severity</th>
                <th>Alert kinds</th>
                <th>Resources</th>
                <th>Lifecycle notices</th>
                <th>Enabled</th>
                {hasRole('admin') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((r) => (
                <tr key={r.id}>
                  <td>{r.priority}</td>
                  <td>{r.name}</td>
                  <td>{r.filters.minSeverity}</td>
                  <td>{describeRuleKinds(r.filters.detectionModes)}</td>
                  <td>{describeRuleResources(r.filters.resourceIds, resourceNames)}</td>
                  <td>
                    {[
                      r.sendResolved ? 'resolved' : null,
                      r.sendAcknowledged ? 'acknowledged' : null,
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </td>
                  <td>{r.enabled ? 'yes' : 'no'}</td>
                  {hasRole('admin') && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setEditing(r)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(r)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {editing !== null && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function RuleModal({
  rule,
  onClose,
  onSaved,
}: {
  rule: WithId<AlertRule> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState(rule?.name ?? '');
  const [priority, setPriority] = useState(String(rule?.priority ?? 100));
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [chainId, setChainId] = useState(rule?.escalationChainId ?? '');
  const [minSeverity, setMinSeverity] = useState(rule?.filters.minSeverity ?? 'warning');
  const [propertyExpression, setPropertyExpression] = useState(
    rule?.filters.propertyExpression ?? ''
  );
  const [modes, setModes] = useState<DetectionMode[]>(
    (rule?.filters.detectionModes as DetectionMode[] | undefined) ?? []
  );
  const [resourceIds, setResourceIds] = useState<string[]>(rule?.filters.resourceIds ?? []);
  const [sendResolved, setSendResolved] = useState(rule?.sendResolved ?? true);
  const [sendAcknowledged, setSendAcknowledged] = useState(
    rule?.sendAcknowledged ?? false
  );
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const chainsQuery = usePickerOptions(['chains'], (tid, startAfter) =>
    chainService.list(tid, { limit: 100, startAfter })
  );

  const resourcesQuery = usePickerOptions(['resources'], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter })
  );
  const resourceOptions = useMemo(() => {
    const known = new Map((resourcesQuery.data?.items ?? []).map((r) => [r.id, r.displayName ?? r.name] as const));
    // A resource the rule names that is no longer listed stays selectable (to clear it), without showing its id.
    const gone = resourceIds.filter((id) => !known.has(id));
    return [
      ...[...known].map(([id, label]) => ({ id, label })),
      ...gone.map((id, i) => ({ id, label: `(removed resource ${i + 1})` })),
    ];
  }, [resourcesQuery.data, resourceIds]);
  const toggleMode = (m: DetectionMode, on: boolean) =>
    setModes((cur) => (on ? [...cur, m] : cur.filter((x) => x !== m)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name,
        priority: Number(priority),
        enabled,
        escalationChainId: chainId,
        filters: {
          minSeverity: minSeverity as 'warning' | 'error' | 'critical',
          // Preserve list filters an API author configured; the UI edits
          // the common ones and must not silently drop the rest.
          ...(rule?.filters.clientIds ? { clientIds: rule.filters.clientIds } : {}),
          ...(rule?.filters.resourceGroups
            ? { resourceGroups: rule.filters.resourceGroups }
            : {}),
          ...(resourceIds.length > 0 ? { resourceIds } : {}),
          ...(rule?.filters.sensorKeys ? { sensorKeys: rule.filters.sensorKeys } : {}),
          ...(rule?.filters.datapoints ? { datapoints: rule.filters.datapoints } : {}),
          // No box ticked = every kind of alert (the filter is left out).
          ...(modes.length > 0 ? { detectionModes: modes } : {}),
          ...(propertyExpression ? { propertyExpression } : {}),
        },
        sendResolved,
        sendAcknowledged,
      };
      if (rule) {
        await ruleService.update(tenantId, rule.id, payload);
      } else {
        await ruleService.create(tenantId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={rule ? `Edit ${rule.name}` : 'New alert rule'} onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="rule-name">Name</label>
          <input
            id="rule-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="rule-priority">Priority (lower = evaluated first)</label>
          <input
            id="rule-priority"
            name="priority"
            type="number"
            min={1}
            max={10000}
            required
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="rule-chain">Escalation chain</label>
          <select
            id="rule-chain"
            name="escalationChainId"
            required
            value={chainId}
            onChange={(e) => setChainId(e.target.value)}
          >
            <option value="">— select —</option>
            {(chainsQuery.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {chainsQuery.data?.truncated && (
            <p className="text-muted">Chain list truncated.</p>
          )}
        </div>
        <div className="form-field">
          <label htmlFor="rule-severity">Minimum severity</label>
          <select
            id="rule-severity"
            name="minSeverity"
            value={minSeverity}
            onChange={(e) =>
              setMinSeverity(e.target.value as 'warning' | 'error' | 'critical')
            }
          >
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="critical">critical</option>
          </select>
        </div>
        <fieldset className="form-field">
          <legend>Alert kinds (leave all unticked for every kind)</legend>
          {DETECTION_MODE_OPTIONS.map((o) => (
            <div key={o.value} className="form-field-inline">
              <input
                id={`rule-kind-${o.value}`}
                name={`kind-${o.value}`}
                type="checkbox"
                checked={modes.includes(o.value)}
                onChange={(e) => toggleMode(o.value, e.target.checked)}
              />
              <label htmlFor={`rule-kind-${o.value}`}>
                {o.label} <span className="text-muted">({o.hint})</span>
              </label>
            </div>
          ))}
          <p className="text-muted">{CHANGE_NOT_RECOVERED_DEFAULT}</p>
          <p className="text-muted">{CHANGE_NOT_RECOVERED_OVERRIDE}</p>
        </fieldset>
        <div className="form-field">
          <label htmlFor="rule-resources">Only these resources (leave empty for any resource)</label>
          <select
            id="rule-resources"
            name="resourceIds"
            multiple
            size={6}
            value={resourceIds}
            onChange={(e) => setResourceIds(Array.from(e.target.selectedOptions, (o) => o.value))}
          >
            {resourceOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {resourcesQuery.data?.truncated && (
            <p className="text-muted">Resource list truncated.</p>
          )}
        </div>
        <div className="form-field">
          <label htmlFor="rule-expression">
            Property expression (optional; e.g. system.kind == &quot;device&quot;)
          </label>
          <input
            id="rule-expression"
            name="propertyExpression"
            value={propertyExpression}
            onChange={(e) => setPropertyExpression(e.target.value)}
          />
        </div>
        <div className="form-field form-field-inline">
          <input
            id="rule-resolved"
            name="sendResolved"
            type="checkbox"
            checked={sendResolved}
            onChange={(e) => setSendResolved(e.target.checked)}
          />
          <label htmlFor="rule-resolved">Notify when resolved</label>
        </div>
        <div className="form-field form-field-inline">
          <input
            id="rule-acked"
            name="sendAcknowledged"
            type="checkbox"
            checked={sendAcknowledged}
            onChange={(e) => setSendAcknowledged(e.target.checked)}
          />
          <label htmlFor="rule-acked">Notify when acknowledged</label>
        </div>
        <div className="form-field form-field-inline">
          <input
            id="rule-enabled"
            name="enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="rule-enabled">Enabled</label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : rule ? 'Save' : 'Create'}
        </button>
      </form>
    </Modal>
  );
}
